'use client';

import { cn } from '@/app/_shared/lib/utils';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NAVIGATION_DROPDOWN_PANEL_CLASS, NAVIGATION_DROPDOWN_TRIGGER_CLASS } from './constants';
import type { NavigationDropDownProps } from './interfaces';

export const NavigationDropDown = ({
    label,
    ariaLabel,
    isActive,
    autoOpen,
    disabled,
    showChevron = true,
    containerClassName,
    triggerClassName,
    activeTriggerClassName = 'bg-overlay-white-30',
    inactiveTriggerClassName = 'bg-transparent hover:text-content-muted',
    panelClassName,
    panelLayout = 'navigation',
    panelPositionClassName,
    panelSizeClassName = 'min-w-[225px]',
    children,
}: NavigationDropDownProps) => {
    const [isOpen, setIsOpen] = useState(!!autoOpen);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (autoOpen) {
            setIsOpen(true);
        }
    }, [autoOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!containerRef.current) return;
            // Ignore clicks while this dropdown sits inside a hidden/inert ancestor (e.g. a closed
            // mobile menu panel) — such a click (like the one opening that panel) is not a real
            // "outside click" from the user's perspective and must not silently close this dropdown.
            // Read pointer-events in the capture phase, before React's own bubble-phase click
            // handlers run and possibly update that ancestor's state (e.g. opening the mobile
            // menu) — otherwise this check would observe the post-click state instead.
            if (getComputedStyle(containerRef.current).pointerEvents === 'none') return;
            if (event.target instanceof Node && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('click', handleClickOutside, true);
        }

        return () => document.removeEventListener('click', handleClickOutside, true);
    }, [isOpen]);

    const close = () => setIsOpen(false);
    const panelLayoutClass = panelLayout === 'floating' ? 'absolute z-1' : 'static max-lg:mt-2 lg:absolute lg:z-1';
    const resolvedPanelPositionClassName =
        panelPositionClassName ?? (panelLayout === 'floating' ? 'left-0' : undefined);

    return (
        <div ref={containerRef} className={cn('relative', containerClassName)}>
            <button
                type="button"
                aria-expanded={isOpen}
                aria-label={ariaLabel}
                disabled={disabled}
                className={cn(
                    NAVIGATION_DROPDOWN_TRIGGER_CLASS,
                    isActive ? activeTriggerClassName : inactiveTriggerClassName,
                    disabled && 'pointer-events-none opacity-60',
                    triggerClassName,
                )}
                onClick={() => setIsOpen((currentIsOpen) => !currentIsOpen)}
            >
                {label}
                {showChevron ? (
                    <ChevronDown
                        className={`ml-[10px] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        size={24}
                    />
                ) : null}
            </button>

            <ul
                className={cn(
                    NAVIGATION_DROPDOWN_PANEL_CLASS,
                    panelSizeClassName,
                    panelLayoutClass,
                    resolvedPanelPositionClassName,
                    panelClassName,
                    isOpen ? 'block' : 'hidden',
                )}
            >
                {children(close)}
            </ul>
        </div>
    );
};
